import { t } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { env } from "@/env";
import { toast } from "@/hooks/use-toast";
import { useScreenWakeLock } from "@/hooks/useScreenWakeLock";
import {
  dictationProviderFor,
  resolveDictationLanguage,
} from "@/lib/dictation-language";
import { openDictationStream } from "@/lib/dictationMicrophone";
import { getRealtimeToken } from "@/lib/native-auth";
import { useAuthStore } from "@/store/auth.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { isWholeUtterancePartial } from "@/store/transcriptionStreaming.store";
import { AudioChunkAccumulator } from "./AudioChunkAccumulator";
import { TranscriberClientEvents, TranscriberServerEvents } from "./constants";
import { type PcmCapture, startPcmCapture } from "./pcmCapture";
import { resolveRecordingRoute } from "./recording-route";
import type {
  TranscriptCompletedMessage,
  TranscriptEndMessage,
  TranscriptionProviderName,
  TranscriptPartialMessage,
} from "./types";

// Short-dictation variant of useAudioTranscription (e.g. spoken instructions).
// It talks to the same transcriber socket, but keeps its text in local state
// and never writes to the global transcription/visit stores. The socket is
// only opened while dictating.

export type DictationStatus = "idle" | "connecting" | "listening" | "finishing";
// Why the last dictation ended without finishing normally. All are retryable:
// pressing the mic again starts over with a fresh recording route.
// - permission: the browser (or the user) blocked the microphone.
// - microphone: no usable microphone (missing, busy, failed to open).
// - offline: the device has no network (before starting or while listening).
// - connection: the transcriber socket failed or dropped.
export type DictationError =
  | "microphone"
  | "permission"
  | "offline"
  | "connection";

// Only one dictation may hold the microphone at a time, across every field
// and panel. Starting a new one stops the previous one exactly as if its stop
// button was pressed: its last words still land in its own field.
let activeDictation: { owner: symbol; stop: () => void } | null = null;

// getUserMedia rejects with a DOMException (not always an Error instance).
const isPermissionError = (error: unknown): boolean => {
  const name = (error as { name?: unknown } | null)?.name;
  return (
    name === "NotAllowedError" ||
    name === "PermissionDeniedError" ||
    name === "SecurityError"
  );
};

const isOffline = (): boolean =>
  typeof navigator !== "undefined" && navigator.onLine === false;

// Generous cap for one spoken instruction; the UI shows the elapsed time
// against it and dictation stops by itself when it is reached.
export const DICTATION_MAX_SECONDS = 180;

// Spoken commands must keep the exact words: the session asks the transcriber
// to skip its cleanup step (postProcessing: false), and the final text is the
// raw transcript. Older transcribers ignore the flag, so we never use their
// cleaned transcript:end text and keep the raw transcript:completed segments.

// If the transcriber never sends transcript:end (e.g. nothing was said),
// fall back to the segments received so far.
const FINISH_TIMEOUT_MS = 8000;
// Some transcriber builds send transcript:end after the first spoken segment
// while the user is still talking. That event is ignored until stop is
// pressed; such a server never sends a second one, so after stop we finish
// once no new segment has arrived for this long.
const SETTLE_AFTER_EARLY_END_MS = 3000;
// The microphone is captured from the moment it opens, but the transcriber
// only accepts audio once its provider is ready (about 1.5 s later). Audio
// from that gap is held and sent first, so words spoken right away are kept.
// Bounded: only the most recent seconds are kept if the connection is slow.
export const DICTATION_PRE_READY_MAX_SECONDS = 10;

export type UseDictationOptions = {
  // Transcription language for this field (input code such as "eng" or
  // "fra"). Defaults to the note language, then the interface language;
  // never the visit recorder's Input Language.
  language?: string;
  // Realtime provider for this dictation instead of the language default
  // (radiology dictation always uses Deepgram nova-3-medical for English).
  provider?: TranscriptionProviderName;
  // Longer cap than DICTATION_MAX_SECONDS (a full report dictation).
  maxSeconds?: number;
};

export const useDictation = (
  options: UseDictationOptions = {},
): {
  status: DictationStatus;
  // Final (completed) words of this dictation.
  text: string;
  // Words still being recognised (not final yet); shown, never committed.
  interim: string;
  error: DictationError | null;
  elapsedSeconds: number;
  // The open microphone while dictating (for a level meter).
  stream: MediaStream | null;
  start: () => Promise<void>;
  stop: () => void;
} => {
  const [status, setStatus] = useState<DictationStatus>("idle");
  const [text, setText] = useState("");
  const [interim, setInterim] = useState("");
  const interimRef = useRef<Record<string, string>>({});
  const ownerRef = useRef(Symbol("dictation"));
  const languageRef = useRef(options.language);
  languageRef.current = options.language;
  const providerRef = useRef(options.provider);
  providerRef.current = options.provider;
  const maxSeconds = options.maxSeconds ?? DICTATION_MAX_SECONDS;
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<DictationError | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const socketRef = useRef<Socket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const captureRef = useRef<PcmCapture | null>(null);
  const accumulatorRef = useRef(new AudioChunkAccumulator(80000));
  // PCM captured before provider:ready, oldest first (see
  // DICTATION_PRE_READY_MAX_SECONDS); null once audio streams live.
  const preReadyRef = useRef<{ chunks: Int16Array[]; samples: number } | null>(
    null,
  );
  const finishTimerRef = useRef<number | undefined>(undefined);
  const elapsedTimerRef = useRef<number | undefined>(undefined);
  // True once stop was pressed: only then does transcript:end finish.
  const stoppingRef = useRef(false);
  const serverEndedEarlyRef = useRef(false);
  const rawSegmentsRef = useRef<string[]>([]);
  // Bumped by every cleanup (stop, panel closed/unmounted). A start() still
  // waiting for the token or the microphone permission sees a newer value and
  // gives up, releasing anything it acquired after the cancel.
  const generationRef = useRef(0);
  // Always the latest stop(): used by the cap and by another field starting.
  const stopRef = useRef<() => void>(() => undefined);

  const stopAudio = useCallback((): void => {
    window.clearInterval(elapsedTimerRef.current);
    streamRef.current?.getTracks().forEach((track) => {
      track.stop();
    });
    streamRef.current = null;
    setStream(null);
    // Closes the capture AudioContext; the tracks are stopped above.
    captureRef.current?.stop();
    captureRef.current = null;
    preReadyRef.current = null;
    accumulatorRef.current.reset();
  }, []);

  const release = useCallback((): void => {
    if (activeDictation?.owner === ownerRef.current) activeDictation = null;
  }, []);

  const cleanup = useCallback((): void => {
    generationRef.current += 1;
    release();
    interimRef.current = {};
    setInterim("");
    window.clearTimeout(finishTimerRef.current);
    stoppingRef.current = false;
    stopAudio();
    socketRef.current?.disconnect();
    socketRef.current = null;
    setStatus("idle");
  }, [stopAudio, release]);

  // A network loss while dictating ends it right away (the socket may take
  // a while to notice), keeping the words already shown.
  useEffect(() => {
    if (status !== "connecting" && status !== "listening") return;
    const onOffline = (): void => {
      setError("offline");
      cleanup();
    };
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("offline", onOffline);
    };
  }, [status, cleanup]);

  useEffect(() => cleanup, [cleanup]);

  // Long dictations: the screen must not sleep (that suspends the mic).
  useScreenWakeLock(status === "connecting" || status === "listening");

  const sendChunk = (isLast: boolean): void => {
    socketRef.current?.emit(TranscriberClientEvents.AUDIO_CHUNK, {
      body: accumulatorRef.current.merged,
      isLast,
    });
    accumulatorRef.current.reset();
  };

  // Same capture as the visit recorder: an AudioWorklet (ScriptProcessor
  // fallback) with inaudible output, so a background window cannot slow the
  // audio clock and drop words.
  // Capture starts as soon as the microphone opens; until the provider is
  // ready the audio is held (bounded) and flushed by streamAudio().
  const startAudio = (stream: MediaStream): void => {
    if (captureRef.current) return;
    preReadyRef.current = { chunks: [], samples: 0 };
    const capture = startPcmCapture(stream, (pcm): void => {
      const pending = preReadyRef.current;
      if (pending) {
        pending.chunks.push(pcm);
        pending.samples += pcm.length;
        const max = capture.sampleRate * DICTATION_PRE_READY_MAX_SECONDS;
        while (pending.samples > max && pending.chunks.length > 1) {
          pending.samples -= pending.chunks.shift()?.length ?? 0;
        }
        return;
      }
      accumulatorRef.current.addPcm(pcm);
      if (accumulatorRef.current.isReadyToSend) sendChunk(false);
    });
    captureRef.current = capture;
  };

  // provider:ready: send the held audio first, then stream live.
  const streamAudio = (): void => {
    const pending = preReadyRef.current;
    if (!pending) return;
    preReadyRef.current = null;
    for (const pcm of pending.chunks) {
      accumulatorRef.current.addPcm(pcm);
      if (accumulatorRef.current.isReadyToSend) sendChunk(false);
    }
  };

  // The early transcript:end already came and no second one will: finish
  // when the segments after stop have settled (the text is already shown).
  const settleAfterEarlyEnd = (): void => {
    window.clearTimeout(finishTimerRef.current);
    finishTimerRef.current = window.setTimeout(
      cleanup,
      SETTLE_AFTER_EARLY_END_MS,
    );
  };

  const start = async (): Promise<void> => {
    if (status !== "idle") return;
    if (isOffline()) {
      setError("offline");
      return;
    }
    if (activeDictation && activeDictation.owner !== ownerRef.current) {
      activeDictation.stop();
    }
    activeDictation = {
      owner: ownerRef.current,
      stop: () => {
        stopRef.current();
      },
    };
    const generation = ++generationRef.current;
    const cancelled = (): boolean => generation !== generationRef.current;
    setError(null);
    setText("");
    setInterim("");
    interimRef.current = {};
    rawSegmentsRef.current = [];
    stoppingRef.current = false;
    serverEndedEarlyRef.current = false;
    setElapsedSeconds(0);
    setStatus("connecting");
    // What a throw below means for the user's message.
    let failure: DictationError = "connection";

    try {
      const token = await getRealtimeToken();
      if (cancelled()) return;
      failure = "microphone";
      let stream: MediaStream;
      try {
        // Settings > Dictation microphone (e.g. a headset while another
        // input records the visit); a missing one falls back with a notice.
        const opened = await openDictationStream({
          recordingDeviceId:
            useRecordingSettingsStore.getState().micDevice?.value,
        });
        stream = opened.stream;
        if (opened.fellBack)
          toast({
            title: t("settings.transcription.dictationMicrophone.fallback"),
          });
      } catch (error) {
        if (isPermissionError(error)) failure = "permission";
        throw error;
      }
      if (cancelled()) {
        // Permission granted after the panel was closed: release the mic.
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        return;
      }
      streamRef.current = stream;
      setStream(stream);
      // Hold what is said while the transcriber connects.
      startAudio(stream);
      failure = "connection";

      const { userData } = useAuthStore.getState();
      const inputLanguage = resolveDictationLanguage(languageRef.current);
      const transcriptionProvider =
        providerRef.current ?? dictationProviderFor(inputLanguage);
      if (!token) throw Error("Authentication required");
      const route = await resolveRecordingRoute({});
      if (cancelled()) return;
      const socket = io(env.VITE_TRANSCRIPTION_SOCKET_URL, {
        path: route?.path ?? env.VITE_TRANSCRIPTION_SOCKET_PATH,
        auth: {
          nativeSessionToken: token,
          ...(route ? { reservationId: route.reservationId } : {}),
        },
        withCredentials: true,
        // One Manager per dictation, never a cached/shared one: a Manager's
        // options (including the slot path) are fixed when it is created.
        forceNew: true,
        // A reservation is single-use and the transcriber ends the session
        // when the socket drops, so an automatic reconnect could only resend
        // a spent reservation (error ROUTE_UNAVAILABLE), and it would redial
        // the Manager's stored path, the old slot after an A/B switch. Stop
        // instead; the mic button starts over with a fresh route.
        reconnection: false,
      });
      socketRef.current = socket;

      // The socket broke (server error, provider gone, connection lost).
      // Keep the words already shown. While listening this is an error the
      // user can retry; after stop it only ends the wait for the last words.
      const fail = (): void => {
        if (cancelled()) return;
        if (!stoppingRef.current || rawSegmentsRef.current.length === 0) {
          setError("connection");
        }
        cleanup();
      };

      let audioStarted = false;
      socket.on("connect", () => {
        if (cancelled()) return;
        // Never send the (spent) reservation a second time.
        if (audioStarted) {
          fail();
          return;
        }
        audioStarted = true;
        socket.emit(TranscriberClientEvents.AUDIO_START, {
          inputLanguage,
          userEmail: userData?.email,
          transcriptionProvider,
          postProcessing: false,
        });
      });
      socket.on(TranscriberServerEvents.PROVIDER_READY, () => {
        if (cancelled()) {
          socket.disconnect();
          return;
        }
        setStatus("listening");
        streamAudio();
        const startedAt = Date.now();
        elapsedTimerRef.current = window.setInterval(() => {
          setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
        }, 1000);
      });
      socket.on(
        TranscriberServerEvents.TRANSCRIPT_COMPLETED,
        (data: TranscriptCompletedMessage) => {
          // A completed segment replaces its own partial words.
          if (data.itemId && data.itemId in interimRef.current) {
            delete interimRef.current[data.itemId];
          } else {
            interimRef.current = {};
          }
          setInterim(Object.values(interimRef.current).join(" ").trim());
          rawSegmentsRef.current.push(data.transcription);
          setText((previous) => `${previous} ${data.transcription}`.trim());
          if (stoppingRef.current && serverEndedEarlyRef.current) {
            settleAfterEarlyEnd();
          }
        },
      );
      // OpenAI sends word deltas per item; Deepgram and Speechmatics send
      // the whole utterance each time (told apart by item id, since the
      // server may route to another provider than the one asked for).
      socket.on(
        TranscriberServerEvents.TRANSCRIPT_PARTIAL,
        (data: TranscriptPartialMessage) => {
          if (cancelled() || !data?.transcription) return;
          const key = data.itemId ?? "partial";
          const previous = interimRef.current[key] ?? "";
          interimRef.current[key] = isWholeUtterancePartial(data.itemId)
            ? data.transcription
            : previous + data.transcription;
          setInterim(Object.values(interimRef.current).join(" ").trim());
        },
      );
      socket.on(
        TranscriberServerEvents.TRANSCRIPT_END,
        (data: TranscriptEndMessage) => {
          if (!stoppingRef.current) {
            // Not asked to stop: keep listening (see SETTLE_AFTER_EARLY_END_MS).
            serverEndedEarlyRef.current = true;
            return;
          }
          const raw =
            data.rawTranscription?.trim() ||
            rawSegmentsRef.current.join(" ").trim();
          if (raw) setText(raw);
          cleanup();
        },
      );
      socket.on(TranscriberServerEvents.CONNECT_ERROR, fail);
      socket.on(TranscriberServerEvents.ERROR, fail);
      socket.on(TranscriberServerEvents.PROVIDER_UNAVAILABLE, fail);
      // Our own cleanup disconnects after bumping the generation, so only an
      // unexpected drop reaches fail().
      socket.on(TranscriberServerEvents.DISCONNECT, fail);
    } catch {
      if (cancelled()) return;
      setError(isOffline() ? "offline" : failure);
      cleanup();
    }
  };

  const stop = (): void => {
    if (status !== "listening") {
      cleanup();
      return;
    }
    // The microphone is free for another field from now on.
    release();
    stoppingRef.current = true;
    setStatus("finishing");
    sendChunk(true);
    stopAudio();
    if (serverEndedEarlyRef.current) settleAfterEarlyEnd();
    else finishTimerRef.current = window.setTimeout(cleanup, FINISH_TIMEOUT_MS);
  };

  // Stop by itself at the cap, exactly as if the user pressed stop.
  stopRef.current = stop;
  useEffect(() => {
    if (status === "listening" && elapsedSeconds >= maxSeconds) {
      stopRef.current();
    }
  }, [status, elapsedSeconds, maxSeconds]);

  return { status, text, interim, error, elapsedSeconds, stream, start, stop };
};
